<template>
  <div class="flex-1 pb-6 px-6 pt-3">
    <!-- Header Section -->
    <div class="bg-background border-b border-border px-8 py-2 -mx-6 mb-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-xl font-bold text-foreground">Customers</h1>
          <p class="text-muted-foreground text-sm mt-1">Manage customers and businesses</p>
        </div>
        <div class="flex items-center space-x-3">
          <button 
            type="button"
            class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 font-semibold"
            @click="showCreateLeadModal = true"
          >
            Create Customer
          </button>
          <router-link 
            v-if="routeName !== 'agency.businesses.index' && routeName !== 'new-agency.businesses.index'" 
            :to="{ name: 'agency.businesses.index', query: { 'create_customer': true } }" 
            class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 font-semibold"
          >
            Create Customer Manually
          </router-link>
          <vue-json-to-csv :json-data="customers" :csv-title="csvTitle()">
            <button class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 py-2 bg-foreground hover:bg-foreground/90 text-background font-semibold shadow-lg hover:shadow-xl transition-all duration-200 px-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-2 h-4 w-4">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" x2="12" y1="15" y2="3"></line>
              </svg>
              Export
            </button>
          </vue-json-to-csv>
        </div>
      </div>
    </div>
    <hr class="mb-6">

    <!-- Main Content -->
    <div class="px-2">
      <div class="bg-card rounded-2xl border border-border overflow-hidden customers-card">
        <!-- Table Controls -->
        <div class="p-6 border-b border-border">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <div class="flex items-center space-x-2">
                <span class="text-sm text-muted-foreground">Show</span>
                <b-form-select
                    v-model="datatable.queries.per_page"
                    class="h-9 rounded-md border border-input bg-background px-3 text-sm modern-select"
                    :options="[10, 15, 25, 50, 100]"
                    @change="getBusinesses"
                ></b-form-select>
                <span class="text-sm text-muted-foreground">entries</span>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <span class="text-sm text-muted-foreground">Search:</span>
              <div class="relative">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.3-4.3"></path>
                </svg>
                <b-form-input
                    v-model="datatable.queries.search"
                    type="text"
                    placeholder="Search..."
                    class="modern-search-input"
                    @keyup.enter="getBusinesses"
                ></b-form-input>
              </div>
            </div>
          </div>
        </div>

        <!-- Table -->
        <template v-if="!loadingBusinesses">
          <div class="relative w-full overflow-auto">
            <table class="w-full caption-bottom text-sm modern-table">
              <thead>
                <tr class="border-b transition-colors bg-muted/50">
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-20">#</th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Date</th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Business</th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Full Name</th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Email</th>
                  <th class="h-12 px-4 align-middle font-medium text-muted-foreground text-center">Connected</th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Phone</th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Status</th>
                  <th class="h-12 px-4 align-middle font-medium text-muted-foreground text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="customer in customers" 
                  :key="customer.id"
                  class="border-b transition-colors hover:bg-muted/30"
                >
                  <td class="p-4 align-middle font-medium">{{ customer.id }}</td>
                  <td class="p-4 align-middle text-muted-foreground">{{ customer.small_date }}</td>
                  <td class="p-4 align-middle font-medium">{{ customer.business_name }}</td>
                  <td class="p-4 align-middle">{{ customer.full_name }}</td>
                  <td class="p-4 align-middle text-muted-foreground">{{ customer.email }}</td>
                  <td class="p-4 align-middle text-center">
                    <svg v-if="customer.location" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-success inline-block">
                      <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                      <path d="m9 11 3 3L22 4"></path>
                    </svg>
                  </td>
                  <td class="p-4 align-middle text-muted-foreground">{{ customer.phone_number }}</td>
                  <td class="p-4 align-middle">
                    <span 
                      v-if="customer.active"
                      class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-success/10 text-success border-success/20"
                    >
                      Active
                    </span>
                    <span 
                      v-else
                      class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-muted/50 text-muted-foreground border-border"
                    >
                      Inactive
                    </span>
                  </td>
                  <td class="p-4 align-middle text-right">
                    <div class="flex items-center justify-end space-x-2">
                      <router-link
                        :to="{
                          name: 'agency.customers.edit',
                          params: { id: customer.id },
                        }"
                        class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 rounded-md h-8 px-2 text-muted-foreground hover:text-foreground hover:bg-muted"
                      >
                        Edit
                      </router-link>
                      <button
                        v-if="customer.email"
                        :disabled="loadingImpersonate"
                        class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:text-accent-foreground h-8 px-3 rounded-full bg-foreground text-background hover:bg-foreground/90 font-medium"
                        @click="impersonate(customer.user_id)"
                      >
                        <b-spinner v-if="loadingImpersonate" small />
                        <span v-else>Login</span>
                      </button>
                      <button
                        :disabled="loadingRemove === customer.id"
                        class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 delete-button h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                        @click="remove(customer)"
                      >
                        <b-spinner v-if="loadingRemove === customer.id" small />
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trash2 h-4 w-4">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          <line x1="10" x2="10" y1="11" y2="17"></line>
                          <line x1="14" x2="14" y1="11" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Pagination -->
          <div class="p-4 border-t border-border flex items-center justify-between">
            <div class="text-sm text-muted-foreground">
              Total {{ totalCustomers }} customers
            </div>
            <div class="flex items-center space-x-2">
              <b-pagination
                v-model="datatable.queries.page"
                :total-rows="totalCustomers"
                :per-page="datatable.queries.per_page"
                class="modern-pagination mb-0"
                @change="onPageChanged"
              ></b-pagination>
            </div>
          </div>
        </template>
        <div v-else class="text-center p-6">
          <b-spinner></b-spinner>
        </div>
      </div>
    </div>

    <!-- Shipping Modal -->
    <b-modal v-model="showShippingModal" :hide-footer="true" :title="`Add/Update Shipping for ${shippingModalOwner.first_name} ${shippingModalOwner.last_name}`" @hidden="resetShippingModal">
      <label>Tracking Number</label>
      <b-input v-model="shippingModalOwner.tracking_number" type="text" />
      <button class="btn btn-success mt-2 w-100" type="button" @click="addShipping">
        <b-spinner v-if="shippingModalOwner.working" small />
        <span v-else>Submit</span>
      </button>
      <table v-if="shippingModalOwner.tracking_numbers.length" class="table">
        <thead>
        <tr>
          <th>Existing Tracking Information</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(tracking,i) in shippingModalOwner.tracking_numbers" :key="i">
          <td>
            <label>Created: {{tracking.front_date}}</label>
            <div class="d-flex">
              <b-input v-model="tracking.tracking_number" />
              <button type="button" class="btn btn-info ml-1" @click="updateTrackingNumber(tracking)">
                <b-spinner v-if="tracking.is_updating" small />
                <span>Update</span>
              </button>
              <button type="button" class="btn btn-danger ml-1" @click="deleteTrackingNumber(tracking)">
                <b-spinner v-if="tracking.is_deleting" small />
                <i v-else class="uil uil-trash-alt" />
              </button>
            </div>
            <div v-if="tracking.tracking_number === null" class="text-danger">
              Not Shipped
            </div>
            <div v-else>
              <div>
                Carrier: {{tracking.carrier}}
              </div>
              <a :href="tracking.tracking_url" target="_blank">Track: {{tracking.tracking_number}}</a>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </b-modal>

    <!-- Delete Modal -->
    <b-modal v-model="showDeleteModal" title="Delete Customer">
      <p v-if="selectedBusiness"
        >Are you sure you want to delete business "{{
          selectedBusiness.business_name
        }}"?</p
      >
      <template v-slot:modal-footer>
        <b-button variant="light" @click="showDeleteModal = false"
          >Cancel</b-button
        >
        <b-button variant="danger" @click="handleRemove">Delete</b-button>
      </template>
    </b-modal>

    <!-- Welcome Modal -->
    <b-modal v-model="showWelcomeModal" size="lg" title="Welcome">
      <div class="text-center">
        <h4 class="text-success">Thanks For Signing Up For ReplyEngine</h4>
        <p>
        </p>
      </div>
      <template v-slot:modal-footer>
        <b-button variant="success" @click="showWelcomeModal = false"
          >Ok</b-button
        >
      </template>
    </b-modal>

    <!-- Create Lead Modal -->
    <b-modal v-model="showCreateLeadModal" size="lg" :hide-footer="true" :hide-header="true" modal-class="widget-modal">
      <div class="text-center">
        <h4>Create Lead</h4>
        <p>
          <reply-engine-generator
              v-if="user && user.agency"
              ref="rlg"
              :search-text="widgetDefaultText"
              class="widget-creator"
              :agency-id="user.agency.id"
              :starting-step="1"
              header-text="Search For Your Business"
              background-color="#ffffff"
              main-text-color="#222222"
              primary-button-background-color="#34a853"
              primary-button-text-color="#ffffff"
              :google-api-key="user.agency.landing_page.admin_settings.google_api_key"
              place-holder="Search"
              :manually-created="true"
              button-text="Create Lead"
              :force-busy="createIsBusy"
              @prospectCreated="prospectCreated"
          ></reply-engine-generator>
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import VueJsonToCsv from 'vue-json-to-csv'
import ReplyEngineGenerator from '../../../../embed-form/src/components/Form'
export default {
  components:{
    VueJsonToCsv,
    'reply-engine-generator': ReplyEngineGenerator
  },
  data() {
    return {
      json_export_labels:{

      },
      datatable: {
        items: [],
        totalItems: 1,
        columns: [
          { key: 'id', label: '#', sortable: true },
          { key: 'small_date', label: 'Date', sortable: true },
          { key: 'business_name', label: 'Business', sortable: true },
          { key: 'full_name', sortable: true },
          { key: 'email', label: 'Email', sortable: true },
          { key: 'connected', label: 'Connected', class: 'text-center'},
          { key: 'phone_number', label: 'Phone', sortable: true },
          { key: 'active', label: 'Status', sortable: true },
          { key: 'actions', label: 'Actions', class:'actions' },
        ],
        queries: {
          per_page: 10,
          page: 1,
          status: 'active',
          prospect_status:'all',
          search: null,
          sort_by: 'id',
          sort_desc: true,
        },
      },
      showDeleteModal: false,
      selectedBusiness: null,
      loadingBusinesses: false,
      loadingImpersonate: false,
      loadingRemove: null,
      showWelcomeModal: false,
      createIsBusy:false,
      widgetDefaultText:'',
      showCreateLeadModal:false,
      showShippingModal:false,
      shippingModalOwner: {
        first_name:'',
        last_name:'',
        id:null,
        tracking_number:'',
        working:false,
        tracking_numbers:[]
      }
    }
  },

  computed: {
    customers() {
      return this.$store.getters['customers/all'] || []
    },

    totalCustomers() {
      return this.$store.getters['customers/total']
    },

    reload() {
      return this.$store.getters['customers/reload']
    },

    user() {
      return this.$store.getters['auth/user']
    },

    routeName(){
      return this.$route.name;
    }
  },

  watch: {
    reload: (val) => {
      console.log(val)
    },
  },

  created() {
    this.$store.dispatch('customers/setCurrentPage', 'index')
  },

  mounted() {
    this.getBusinesses();

    if (this.user.agency && this.user.agency.is_new) {
      this.showWelcomeModal = true

      this.$store.dispatch('agency/toggleIsNew', this.user.agency)
    }

    if(window.location.search.includes('create_lead')){
      this.showCreateLeadModal = true;
      let query = Object.assign({}, this.$route.query);
      delete query.create_lead;
      if(query.hasOwnProperty('business_name')) {
        this.widgetDefaultText = decodeURI(query.business_name);
        delete query.business_name;
      }
      this.$router.replace({ query });
    }
  },

  methods: {
    updateTrackingNumber(tracking){
      tracking.is_updating = true;
      this.$store
          .dispatch('auth/updateTrackingNumber', tracking)
          .then((resp) => {
            tracking.is_updating = false;
          })
          .catch(() => {
            tracking.is_updating = false;
          })
    },
    deleteTrackingNumber(tracking){
      tracking.is_deleting = true;
      this.$store
          .dispatch('auth/deleteTrackingNumber', tracking.id)
          .then((resp) => {
            this.shippingModalOwner.tracking_numbers = resp.data.tracking_numbers;
          })
          .catch(() => {

          })
    },
    resetShippingModal(){
      this.shippingModalOwner = {
        first_name:'',
        last_name:'',
        id:null,
        tracking_number:'',
        working:false,
        tracking_numbers:[]
      };
    },
    updateShipping(business){
      this.shippingModalOwner.id = business.user_id;
      this.shippingModalOwner.first_name = business.first_name;
      this.shippingModalOwner.last_name = business.last_name;
      this.showShippingModal = true;
      this.$store
          .dispatch('auth/getUsersTrackingNumber', this.shippingModalOwner.id)
          .then((resp) => {
            this.shippingModalOwner.tracking_numbers = resp.data.tracking_numbers;
          })
          .catch(() => {

          })
    },
    addShipping(){
      this.shippingModalOwner.working = true;
      this.$store
          .dispatch('auth/addTrackingNumber', this.shippingModalOwner)
          .then((resp) => {
            this.shippingModalOwner.working = false;
            this.shippingModalOwner.tracking_numbers = resp.data.tracking_numbers;
            this.shippingModalOwner.tracking_number = '';
          })
          .catch(() => {
            this.shippingModalOwner.working = false
          })
    },
    prospectCreated(business){
      this.showCreateLeadModal = false;
      this.createIsBusy = false;
      this.$router.push({
        name: 'agency.businesses.edit',
        params: { id: business.id },
      })
    },
    csvTitle(){
      return 'reply-engine-export-'+Date.now()
    },
    sortChanged(args) {
      this.datatable.queries.sort_by = args.sortBy
      this.datatable.queries.sort_desc = args.sortDesc
      this.datatable.queries.page = 1
      this.getBusinesses()
    },

    onPageChanged(page) {
      this.datatable.queries.page = page
      this.getBusinesses()
    },

    getBusinesses() {
      this.loadingBusinesses = true

      this.$store
        .dispatch('customers/getAll', {
          queries: this.datatable.queries,
          page: 'index',
        })
        .then((res) => {
          this.loadingBusinesses = false
        })
        .catch(() => {
          this.loadingBusinesses = false
        })
    },

    impersonate(userId) {
      this.loadingImpersonate = true

      this.$store
        .dispatch('auth/impersonate', userId)
        .then((res) => {
          this.$store
            .dispatch('auth/getProfile')
            .then((user) => {
              this.$router.push('/' + user.role.toLocaleLowerCase())
              this.loadingImpersonate = false
            })
            .catch(() => {
              this.loadingImpersonate = false
            })
        })
        .catch(() => {
          this.loadingImpersonate = false
        })
    },

    remove(business) {
      this.showDeleteModal = true
      this.selectedBusiness = business
    },

    handleRemove() {
      this.showDeleteModal = false
      this.loadingRemove = this.selectedBusiness.id
      this.$store
        .dispatch('business/archive', this.selectedBusiness.id)
        .then(() => {
          this.loadingRemove = null
          this.getBusinesses()
        })
        .catch(() => {
          this.loadingRemove = null
        })
    },
  },
}
</script><style lang="scss" scoped>
.customers-card {
  background-color: #ffffff;
  box-shadow: 0 1px 3px 0 hsl(0 0% 0% /0.1), 0 1px 2px -1px hsl(0 0% 0% / 0.1);
}

.modern-table {
  border-collapse: collapse;
  
  thead tr {
    background-color: rgba(0, 0, 0, 0.025);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }
  
  tbody tr {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.02);
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  th, td {
    vertical-align: middle;
  }
}

.modern-select {
  min-width: 70px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

.modern-search-input {
  height: 2.5rem;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  background-color: #ffffff;
  padding-left: 2.25rem;
  padding-right: 0.75rem;
  font-size: 0.875rem;
  width: 16rem;
  transition: all 0.15s;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
    border-color: rgb(59, 130, 246);
  }
  
  &::placeholder {
    color: rgba(108, 117, 125, 0.9);
  }
}

.bg-foreground {
  background-color: #2d3648;
  color: #fff;
  
  &:hover {
    background-color: rgba(15, 23, 42, 0.9);
  }
}

.text-background {
  color: #fff;
}

.text-success {
  color: #22c55e;
}

.text-destructive {
  color: #ef4444;
}

.text-muted-foreground {
  color: #6b7280;
}

.bg-success\/10 {
  background-color: rgba(34, 197, 94, 0.1);
}

.text-success {
  color: #16a34a;
}

.border-success\/20 {
  border-color: rgba(34, 197, 94, 0.2);
}

.bg-destructive\/10 {
  background-color: rgba(239, 68, 68, 0.1);
}

.-mx-6 {
  margin-left: -1.5rem;
  margin-right: -1.5rem;
}

.text-right {
  text-align: right;
}

.w-20 {
  width: 5rem;
}

// Header spacing
.space-x-3 > * + * {
  margin-left: 0.75rem;
}

// Button hover states
button, a {
  transition: all 0.2s ease;
}

.hover\:bg-accent:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.hover\:text-accent-foreground:hover {
  color: #111;
}

.border-input {
  border-color: #d1d5db;
}

.bg-background {
  background-color: #ffffff;
}

.rounded-full {
  border-radius: 9999px;
}

.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.hover\:shadow-xl:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

// Delete button SVG styling
.delete-button {
  border: none !important;
  background: transparent !important;
  
  svg {
    pointer-events: none;
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }
}
</style><style>
.pac-container.pac-logo {
  z-index: 10000000;
}

/* Modern Pagination Styles */
.modern-pagination.pagination {
  margin-bottom: 0 !important;
  display: flex;
  gap: 0.5rem;
}

.modern-pagination .page-item {
  margin: 0 !important;
}

.modern-pagination .page-item .page-link {
  border-radius: 0.375rem !important;
  border: 1px solid #d1d5db !important;
  background-color: #ffffff !important;
  color: #111 !important;
  font-weight: 500;
  padding: 0.5rem 0.75rem;
  min-width: 2.25rem;
  height: 2.25rem;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  font-size: 0.875rem;
}

.modern-pagination .page-item .page-link:hover:not(.disabled) {
  background-color: rgba(0, 0, 0, 0.05) !important;
  border-color: #d1d5db !important;
  color: #111 !important;
}

.modern-pagination .page-item.active .page-link {
  background-color: rgb(37, 99, 235) !important;
  border-color: rgb(37, 99, 235) !important;
  color: #ffffff !important;
}

.modern-pagination .page-item.active .page-link:hover {
  background-color: rgba(37, 99, 235, 0.9) !important;
}

.modern-pagination .page-item.disabled .page-link {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Additional table refinements */
.bg-card {
  background-color: #ffffff;
}

.border-border {
  border-color: rgba(0, 0, 0, 0.1);
}

.rounded-2xl {
  border-radius: 1rem;
}
</style>